import { useEffect, useRef } from "react";

import type { AlertaHistorico } from "../../lib/alertas/tipos";
import { CANAIS } from "../../lib/alertas/tipos";

/*
 * Modal de detalhe do erro de um alerta em dead_letter (FE-7 — Tela 7 §10.5).
 *
 * Espelha a estrutura do `ConfirmacaoConciliacao`: `role="dialog"` + `aria-modal`,
 * rotulado por `aria-labelledby`, foco movido para o primeiro botao ao abrir e
 * Escape para fechar. Mostra o `ultimo_erro` (ja sanitizado na origem pelo
 * despachante — categorico, sem PII) num bloco destacado.
 *
 * Componente PURO de apresentacao: nao conhece hooks. O orquestrador
 * (`HistoricoAlertas`) injeta `aoFechar`/`aoReagendar` e o booleano `podeReagendar`.
 */

const SEM_VALOR = "—";

/** Resolve o rotulo pt-BR do canal; valor cru se ausente da tupla. */
function rotuloCanal(valor: string): string {
  const par = CANAIS.find(([chave]) => chave === valor);
  return par ? par[1] : valor;
}

interface ModalErroProps {
  alerta: AlertaHistorico;
  /** O usuario pode reagendar (mostra o botao de acao)? */
  podeReagendar: boolean;
  /** Fecha o modal sem efeito. */
  aoFechar: () => void;
  /** Abre o fluxo de reagendamento deste alerta. */
  aoReagendar: () => void;
}

export function ModalErro({
  alerta,
  podeReagendar,
  aoFechar,
  aoReagendar,
}: ModalErroProps): JSX.Element {
  const fecharRef = useRef<HTMLButtonElement>(null);

  // Move o foco para o primeiro botao ao abrir (acessibilidade de modal).
  useEffect(() => {
    fecharRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-modal-erro"
      onKeyDown={(evento) => {
        if (evento.key === "Escape") {
          aoFechar();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-off-black/50 p-4"
    >
      <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
        <h2 id="titulo-modal-erro" className="font-titulo text-xl text-off-black">
          Detalhe do erro
        </h2>

        <p>
          Último erro registrado na tentativa de entrega via{" "}
          {rotuloCanal(alerta.canal)}.
        </p>

        <div className="rounded-md bg-platinum p-4">
          {alerta.ultimo_erro ?? SEM_VALOR}
        </div>

        <div className="flex justify-end gap-3">
          <button
            ref={fecharRef}
            type="button"
            onClick={aoFechar}
            className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black"
          >
            Fechar
          </button>
          {podeReagendar && (
            <button
              type="button"
              onClick={aoReagendar}
              className="rounded-md border border-flame bg-flame px-4 py-1 text-snow"
            >
              Reagendar este alerta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
